import { useMemo } from "react";
import { Alert, Box, Chip, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import FileDropzone from "../../anexosCircuito/fileDropzone";
import { MAX_TOTAL_SIZE_BYTES, formatBytes, getFileSize } from "../../anexosCircuito";
import styles from "./styles";

// Seção de anexos do equipamento selecionado, exibida dentro do NodeInspector
export default function NodeAnexos({
  nodeAnexos = { saved: [], newFiles: [], deletedIds: [], loaded: false, loading: false },
  editMode = false,
  inputId,
  onAddFiles = () => {},
  onRemoveFile = () => {},
  onDownloadFile = () => {},
}) {
  const theme = useTheme();
  const nodeAnexosStyles = styles(theme);

  const savedFiles = nodeAnexos?.saved || [];
  const newFiles = nodeAnexos?.newFiles || [];

  const totalSize = useMemo(
    () =>
      [...savedFiles, ...newFiles].reduce((sum, f) => sum + getFileSize(f), 0),
    [savedFiles, newFiles],
  );
  const exceedsLimit = totalSize > MAX_TOTAL_SIZE_BYTES;

  const handleDrop = (evento) => {
    evento.preventDefault();
    if (!editMode) return;
    onAddFiles(Array.from(evento.dataTransfer.files));
  };

  const handleInputChange = (evento) => {
    onAddFiles(Array.from(evento.target.files));
    evento.target.value = "";
  };

  // Cola (Ctrl+V) é tratado centralmente pelo usePriorityPaste no componente pai (diagramaEquipamentos),
  // que já direciona para onAddFiles com prioridade sobre os Anexos do circuito

  return (
    <Box sx={nodeAnexosStyles.wrapper}>
      {exceedsLimit && (
        <Alert severity="error" sx={nodeAnexosStyles.alert}>
          O tamanho total dos anexos é {formatBytes(totalSize)} e excede o
          limite de {formatBytes(MAX_TOTAL_SIZE_BYTES)} por equipamento.
        </Alert>
      )}

      {editMode && (
        <FileDropzone
          files={newFiles}
          inputId={inputId}
          compact
          onDrop={handleDrop}
          onDragOver={(evento) => evento.preventDefault()}
          onFileChange={handleInputChange}
          onRemoveFile={onRemoveFile}
        />
      )}

      {savedFiles.length > 0 && (
        <Box sx={{ mt: editMode ? 1 : 0 }}>
          <Typography sx={nodeAnexosStyles.savedFilesLabel}>
            Arquivos anexados ({savedFiles.length})
          </Typography>
          <Box sx={nodeAnexosStyles.savedFilesGrid}>
            {savedFiles.map((file) => {
              const fileName = file?.nome || file?.name || "anexo";
              const isPdf =
                file?.mimeType === "application/pdf" ||
                fileName?.toLowerCase?.()?.endsWith(".pdf");
              const isImg =
                file?.mimeType?.startsWith?.("image/") ||
                fileName?.match?.(/\.(jpg|jpeg|png|gif|webp)$/i);
              const sizeBytes = getFileSize(file);
              const sizeLabel =
                sizeBytes > 0
                  ? sizeBytes > 1024 * 1024
                    ? `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
                    : `${(sizeBytes / 1024).toFixed(1)} KB`
                  : "";
              const ext = fileName?.split?.(".")?.pop()?.toUpperCase() || "ARQUIVO";

              return (
                <Box
                  key={file?.id}
                  sx={nodeAnexosStyles.fileCard}
                  onClick={() => onDownloadFile(file)}
                >
                  <Box sx={nodeAnexosStyles.fileIconBox}>
                    {isPdf ? (
                      <PictureAsPdfOutlined sx={{ fontSize: 15 }} />
                    ) : isImg ? (
                      <ImageOutlined sx={{ fontSize: 15 }} />
                    ) : (
                      <InsertDriveFileOutlined sx={{ fontSize: 15 }} />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Tooltip title={fileName} arrow>
                      <Typography sx={nodeAnexosStyles.fileNameText}>
                        {fileName}
                      </Typography>
                    </Tooltip>
                    <Box sx={nodeAnexosStyles.fileMetaRow}>
                      <Chip label={ext} size="small" sx={nodeAnexosStyles.extChip} />
                      {sizeLabel && (
                        <Typography sx={nodeAnexosStyles.sizeLabel}>
                          {sizeLabel}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {editMode && (
                    <Tooltip title="Remover anexo" arrow>
                      <IconButton
                        size="small"
                        sx={nodeAnexosStyles.removeButton}
                        onClick={(evento) => {
                          evento.stopPropagation();
                          onRemoveFile(file);
                        }}
                        aria-label="Remover anexo"
                      >
                        <CloseOutlined fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {savedFiles.length === 0 && newFiles.length === 0 && (
        <Typography sx={nodeAnexosStyles.emptyStateText}>
          {editMode
            ? "Nenhum anexo neste equipamento ainda."
            : "Nenhum anexo neste equipamento."}
        </Typography>
      )}
    </Box>
  );
}
